import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDate, formatDateTime, formatRelative } from '../ui/format';
import { Badge, ErrorState, Loading, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { useAuth } from '../auth/auth-store';
import { Avatar, Person } from '../ui/Avatar';
import { MemberSelect, UserSelect, useMembers } from '../ui/UserSelect';
import {
  addChecklistItem,
  addComment,
  assignTask,
  getTask,
  getTime,
  listComments,
  listProjects,
  logTime,
  removeChecklistItem,
  setTaskStatus,
  startTimer,
  stopTimer,
  toggleChecklistItem,
  unwatchTask,
  updateTask,
  watchTask,
  getTaskActivity,
  listSprints,
  moveToSprint,
  WORK_ITEM_TYPES,
  type ActivityEntry,
  type TaskDetail,
  type TaskPriority,
  type TaskStatus,
  type WorkItemType,
} from './api';

/**
 * One task.
 *
 * The checklist is the part people actually use, so it is first and it is inline — a sub-item is a
 * line of text and a tick, and anything more ceremonious than that stops being worth doing.
 *
 * Time is logged in minutes or measured by a timer. Both exist because both happen: somebody
 * remembering at the end of the day that it took two hours, and somebody starting the clock.
 */

const STATUS_TONE: Record<TaskStatus, BadgeTone> = {
  TODO: 'neutral',
  IN_PROGRESS: 'info',
  BLOCKED: 'danger',
  DONE: 'positive',
  CANCELED: 'neutral',
};

const STATUSES: TaskStatus[] = ['TODO', 'IN_PROGRESS', 'BLOCKED', 'DONE', 'CANCELED'];

const PRIORITIES: TaskPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

/**
 * One control in the task's toolbar, with its name above it.
 *
 * The toolbar used to be a row of bare words — a badge saying "Concluída" beside a dropdown also
 * saying "Concluída", then "Média", then "Tarefa", then an empty box with "pts" in it. Every one of
 * them is a different field, and without a label the row reads as a sentence that does not parse.
 */
function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <label className="toolbar__field">
      <span className="toolbar__label">{label}</span>
      {children}
    </label>
  );
}

export function TaskPage(): React.JSX.Element {
  const { taskId } = useParams<{ taskId: string }>();
  const id = taskId!;
  const client = useQueryClient();

  const [error, setError] = useState<string | null>(null);
  const [item, setItem] = useState('');
  const [comment, setComment] = useState('');
  const [minutes, setMinutes] = useState(30);
  const [note, setNote] = useState('');

  const task = useQuery({ queryKey: ['task', id], queryFn: () => getTask(id) });
  const comments = useQuery({ queryKey: ['task-comments', id], queryFn: () => listComments(id) });
  const time = useQuery({ queryKey: ['task-time', id], queryFn: () => getTime(id) });
  const projects = useQuery({ queryKey: ['projects'], queryFn: listProjects });

  /*
   * The activity feed goes with it.
   *
   * It is built from the task's own audit trail, so anything that changes the task changes the
   * feed — and without this the screen showed a history that stopped just short of the thing the
   * person had done a second earlier, which reads as "it did not save".
   */
  function refresh(): void {
    void client.invalidateQueries({ queryKey: ['task', id] });
    void client.invalidateQueries({ queryKey: ['task-activity', id] });
    void client.invalidateQueries({ queryKey: ['tasks'] });
  }

  function fail(caught: unknown): void {
    setError(messageFor(caught));
  }

  const move = useMutation({
    mutationFn: (next: TaskStatus) =>
      setTaskStatus(id, { status: next, expectedVersion: task.data!.version }),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail,
  });

  const priority = useMutation({
    mutationFn: (input: { priority: TaskPriority }) => updateTask(id, input),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail,
  });

  const addItem = useMutation({
    mutationFn: () => addChecklistItem(id, item.trim()),
    onSuccess: () => {
      setItem('');
      setError(null);
      refresh();
    },
    onError: fail,
  });

  const toggleItem = useMutation({
    mutationFn: (input: { itemId: string; done: boolean }) =>
      toggleChecklistItem(id, input.itemId, input.done),
    onSuccess: refresh,
    onError: fail,
  });

  const dropItem = useMutation({
    mutationFn: (itemId: string) => removeChecklistItem(id, itemId),
    onSuccess: refresh,
    onError: fail,
  });

  const comment_ = useMutation({
    mutationFn: () => addComment(id, comment.trim()),
    onSuccess: () => {
      setComment('');
      setError(null);
      void client.invalidateQueries({ queryKey: ['task-comments', id] });
      void client.invalidateQueries({ queryKey: ['task-activity', id] });
    },
    onError: fail,
  });

  const log = useMutation({
    mutationFn: () => logTime(id, { minutes, ...(note.trim() ? { note: note.trim() } : {}) }),
    onSuccess: () => {
      setNote('');
      setError(null);
      void client.invalidateQueries({ queryKey: ['task-time', id] });
      refresh();
    },
    onError: fail,
  });

  const timer = useMutation({
    mutationFn: (action: 'start' | 'stop') => (action === 'start' ? startTimer(id) : stopTimer()),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['task-time', id] });
      refresh();
    },
    onError: fail,
  });

  if (task.error) {
    return (
      <div className="view">
        <ErrorState error={task.error} onRetry={() => void task.refetch()} />
      </div>
    );
  }

  if (!task.data) {
    return (
      <div className="view">
        <Loading />
      </div>
    );
  }

  const detail = task.data;
  const project = projects.data?.projects.find((entry) => entry.id === detail.project_id);
  const done = detail.checklist.filter((entry) => entry.done).length;

  return (
    <div className="view">
      <PageHeader
        title={
          project?.key && detail.number !== null
            ? `${project.key}-${detail.number} · ${detail.title}`
            : detail.title
        }
        description={project?.name ?? t('work.noProject')}
        actions={
          <Link className="button button--quiet button--compact" to="/trabalho/tarefas">
            {t('record.backToList')}
          </Link>
        }
      />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {/*
       * Everything about the task that is one word, in one row, each under its own name.
       *
       * The status is a dropdown and not also a badge beside it: the badge repeated the word the
       * dropdown was already showing, so a finished task read "Concluída Concluída". The colour
       * that badge carried is on the dropdown instead, which is the thing somebody looks at.
       */}
      <div className="toolbar toolbar--fields">
        <Field label={t('work.field.status')}>
          <select
            className={`select select--tone-${STATUS_TONE[detail.status]}`}
            value={detail.status}
            disabled={move.isPending}
            onChange={(event) => move.mutate(event.target.value as TaskStatus)}
          >
            {STATUSES.map((entry) => (
              <option key={entry} value={entry}>
                {t(`work.task.${entry}`)}
              </option>
            ))}
          </select>
        </Field>

        {/* Editable, like everything else here: it was the one field on this row somebody could
            read and not change, which is not a distinction anybody expects. */}
        <Field label={t('work.field.priority')}>
          <select
            className="select select--compact"
            value={detail.priority}
            disabled={priority.isPending}
            onChange={(event) => priority.mutate({ priority: event.target.value as TaskPriority })}
          >
            {PRIORITIES.map((entry) => (
              <option key={entry} value={entry}>
                {t(`work.priority.${entry}`)}
              </option>
            ))}
          </select>
        </Field>

        <Delivery detail={detail} usesSprints={project?.uses_sprints ?? false} onError={fail} />

        {detail.due_date && (
          <Field label={t('work.field.dueDate')}>
            <span className="toolbar__value">{formatDate(detail.due_date)}</span>
          </Field>
        )}
      </div>

      <div className="record">
        <div className="stack">
          <Panel
            title={`${t('work.checklist')} ${
              detail.checklist.length > 0 ? `· ${done}/${detail.checklist.length}` : ''
            }`}
          >
            {detail.checklist.length === 0 ? (
              <p className="muted">{t('work.noChecklist')}</p>
            ) : (
              <ul className="checklist">
                {detail.checklist.map((entry) => (
                  <li key={entry.id}>
                    <label className={entry.done ? 'checklist__done' : ''}>
                      <input
                        type="checkbox"
                        checked={entry.done}
                        disabled={toggleItem.isPending}
                        onChange={(event) =>
                          toggleItem.mutate({ itemId: entry.id, done: event.target.checked })
                        }
                      />
                      {entry.title}
                    </label>

                    <button
                      type="button"
                      className="checklist__remove"
                      aria-label={t('work.removeItem')}
                      onClick={() => dropItem.mutate(entry.id)}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <form
              className="toolbar"
              onSubmit={(event) => {
                event.preventDefault();
                if (item.trim()) addItem.mutate();
              }}
            >
              <label className="field-search">
                <input
                  value={item}
                  placeholder={t('work.newChecklistItem')}
                  onChange={(event) => setItem(event.target.value)}
                />
              </label>

              <button
                type="submit"
                className="button button--quiet button--compact"
                disabled={item.trim() === '' || addItem.isPending}
              >
                {t('work.addItem')}
              </button>
            </form>
          </Panel>

          <Panel title={t('work.people')}>
            <People taskId={id} onError={fail} />
          </Panel>

          <Panel title={t('work.time')}>
            <p className="metric__value">{hours(time.data?.total_minutes ?? 0)}</p>

            <div className="toolbar">
              <button
                type="button"
                className="button button--quiet button--compact"
                disabled={timer.isPending}
                onClick={() => timer.mutate('start')}
              >
                {t('work.startTimer')}
              </button>

              <button
                type="button"
                className="button button--quiet button--compact"
                disabled={timer.isPending}
                onClick={() => timer.mutate('stop')}
              >
                {t('work.stopTimer')}
              </button>
            </div>

            <form
              className="toolbar"
              onSubmit={(event) => {
                event.preventDefault();
                log.mutate();
              }}
            >
              <input
                className="select"
                type="number"
                min={1}
                max={1440}
                value={minutes}
                aria-label={t('work.minutes')}
                onChange={(event) => setMinutes(Number(event.target.value))}
              />

              <label className="field-search">
                <input
                  value={note}
                  placeholder={t('work.timeNote')}
                  onChange={(event) => setNote(event.target.value)}
                />
              </label>

              <button
                type="submit"
                className="button button--quiet button--compact"
                disabled={log.isPending}
              >
                {t('work.logTime')}
              </button>
            </form>

            {(time.data?.entries.length ?? 0) > 0 && (
              <ul className="home-list">
                {(time.data?.entries ?? []).map((entry) => (
                  <li key={entry.id}>
                    <span className="cell-stack">
                      <span className="strong">{hours(entry.minutes)}</span>
                      {entry.note && <span className="muted small">{entry.note}</span>}
                    </span>
                    <span className="muted small">{formatDateTime(entry.started_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          {(detail.depends_on.length > 0 || detail.blocks.length > 0) && (
            <Panel title={t('work.dependencies')}>
              {detail.depends_on.length > 0 && (
                <>
                  <h3 className="record__subhead">{t('work.dependsOn')}</h3>
                  <ul className="home-list">
                    {detail.depends_on.map((link) => (
                      <li key={link.id}>
                        <Link to={`/trabalho/tarefas/${link.id}`}>
                          <span>{link.title}</span>
                          <Badge tone={STATUS_TONE[link.status]}>
                            {t(`work.task.${link.status}`)}
                          </Badge>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {detail.blocks.length > 0 && (
                <>
                  <h3 className="record__subhead">{t('work.blocks')}</h3>
                  <ul className="home-list">
                    {detail.blocks.map((link) => (
                      <li key={link.id}>
                        <Link to={`/trabalho/tarefas/${link.id}`}>
                          <span>{link.title}</span>
                          <Badge tone={STATUS_TONE[link.status]}>
                            {t(`work.task.${link.status}`)}
                          </Badge>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </Panel>
          )}
        </div>

        <div className="stack">
          <Panel title={t('work.activity')}>
            <Activity
              taskId={id}
              projectId={detail.project_id}
              usesSprints={project?.uses_sprints ?? false}
            />
          </Panel>

          <Panel title={t('work.comments')}>
            <form
              className="stack"
              onSubmit={(event) => {
                event.preventDefault();
                if (comment.trim()) comment_.mutate();
              }}
            >
              <textarea
                className="record__note"
                rows={2}
                value={comment}
                placeholder={t('work.commentPlaceholder')}
                onChange={(event) => setComment(event.target.value)}
              />

              <button
                type="submit"
                className="button button--primary button--compact"
                disabled={comment.trim() === '' || comment_.isPending}
              >
                {t('work.comment')}
              </button>
            </form>

            {comments.isLoading ? (
              <Loading />
            ) : (comments.data?.comments.length ?? 0) === 0 ? (
              <p className="muted">{t('work.noComments')}</p>
            ) : (
              <ol className="timeline">
                {(comments.data?.comments ?? []).map((entry) => (
                  <li key={entry.id} className="timeline__item">
                    <span className="timeline__marker" aria-hidden="true" />
                    <div className="timeline__body">
                      {/* Who said it, beside when. A comment whose author is an id is a comment
                        somebody has to go and look up before they can weigh it. */}
                      <span className="cell-inline">
                        <Author userId={entry.author_id} />
                        <span className="muted small">{formatDateTime(entry.created_at)}</span>
                      </span>
                      <p className="timeline__text">{entry.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}

/**
 * Type, points and sprint — the three fields a delivery team argues about.
 *
 * Shown inline on the toolbar rather than behind an edit dialog, because re-pointing a story in
 * planning is a ten-second act and a modal turns it into a decision. The sprint select only appears
 * for a project that uses sprints: for everybody else it would be a control with one option.
 */
function Delivery({
  detail,
  usesSprints,
  onError,
}: {
  detail: TaskDetail;
  usesSprints: boolean;
  onError: (caught: unknown) => void;
}): React.JSX.Element {
  const client = useQueryClient();
  const [points, setPoints] = useState(
    detail.story_points === null ? '' : String(detail.story_points),
  );

  const sprints = useQuery({
    queryKey: ['sprints', detail.project_id],
    queryFn: () => listSprints(detail.project_id!),
    enabled: usesSprints && detail.project_id !== null,
  });

  function refresh(): void {
    void client.invalidateQueries({ queryKey: ['task', detail.id] });
    void client.invalidateQueries({ queryKey: ['task-activity', detail.id] });
    void client.invalidateQueries({ queryKey: ['tasks'] });
    void client.invalidateQueries({ queryKey: ['backlog'] });
  }

  const change = useMutation({
    mutationFn: (input: Parameters<typeof updateTask>[1]) => updateTask(detail.id, input),
    onSuccess: refresh,
    onError,
  });

  const move = useMutation({
    mutationFn: (sprintId: string | null) => moveToSprint(detail.id, sprintId),
    onSuccess: refresh,
    onError,
  });

  const open = (sprints.data?.sprints ?? []).filter((sprint) => sprint.state !== 'COMPLETED');

  return (
    <>
      <Field label={t('work.itemType')}>
        <select
          className="select select--compact"
          value={detail.item_type}
          disabled={change.isPending}
          onChange={(event) => change.mutate({ itemType: event.target.value as WorkItemType })}
        >
          {WORK_ITEM_TYPES.map((value) => (
            <option key={value} value={value}>
              {t(`work.item.${value}`)}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t('sprint.points')}>
        <input
          className="points-input"
          value={points}
          inputMode="decimal"
          placeholder={t('sprint.pointsShort')}
          onChange={(event) => setPoints(event.target.value)}
          onBlur={() => {
            const trimmed = points.trim();
            const next = trimmed === '' ? null : Number(trimmed.replace(',', '.'));

            if (next !== null && Number.isNaN(next)) {
              setPoints(detail.story_points === null ? '' : String(detail.story_points));
              return;
            }

            if (next !== detail.story_points) change.mutate({ storyPoints: next });
          }}
        />
      </Field>

      {usesSprints && (
        <Field label={t('sprint.moveTo')}>
          <select
            className="select select--compact"
            value={detail.sprint_id ?? ''}
            disabled={move.isPending}
            onChange={(event) => move.mutate(event.target.value || null)}
          >
            <option value="">{t('sprint.waiting')}</option>
            {open.map((sprint) => (
              <option key={sprint.id} value={sprint.id}>
                {sprint.name}
              </option>
            ))}
            {detail.sprint_id && !open.some((sprint) => sprint.id === detail.sprint_id) && (
              <option value={detail.sprint_id}>{t('sprint.state.COMPLETED')}</option>
            )}
          </select>
        </Field>
      )}
    </>
  );
}

/**
 * Who did what to this item.
 *
 * One list out of three sources — field changes, comments and logged time — because "who added
 * this, who moved it, who closed it" is one question and answering it from three tabs is not
 * answering it. A change reads as a sentence with the old and new value, so nobody has to open a
 * diff to learn that the due date moved.
 */
function Activity({
  taskId,
  projectId,
  usesSprints,
}: {
  taskId: string;
  projectId: string | null;
  usesSprints: boolean;
}): React.JSX.Element {
  const { options } = useMembers();

  const sprints = useQuery({
    queryKey: ['sprints', projectId],
    queryFn: () => listSprints(projectId!),
    enabled: usesSprints && projectId !== null,
  });

  const feed = useQuery({
    queryKey: ['task-activity', taskId],
    queryFn: () => getTaskActivity(taskId),
  });

  if (feed.isLoading) return <Loading />;
  if (feed.error) return <ErrorState error={feed.error} onRetry={() => void feed.refetch()} />;

  const entries = feed.data?.activity ?? [];

  if (entries.length === 0) return <p className="muted">{t('work.noActivity')}</p>;

  const who = (entry: ActivityEntry): string => {
    if (entry.actorType !== 'USER') return t(`audit.actor.${entry.actorType}`);

    const found =
      entry.actorId === null ? undefined : options.find((person) => person.id === entry.actorId);

    return found?.name ?? t('work.systemAuthor');
  };

  /*
   * An id is not a thing that happened to somebody.
   *
   * The two ids a task change can carry are a person and a sprint, and both are already on this
   * screen by name — so they are resolved here. Anything else that looks like a uuid says that it
   * was set or cleared rather than printing thirty-six characters nobody can read.
   */
  const names = new Map<string, string>([
    ...options.map((person) => [person.id, person.name] as const),
    ...(sprints.data?.sprints ?? []).map((sprint) => [sprint.id, sprint.name] as const),
  ]);

  /*
   * What happened to the task, without the comments (Phase 118).
   *
   * The comments have a panel of their own on this screen, with the composer and the author's
   * face; repeating each of them here means every comment is on the page twice, which is the same
   * complaint that started this. The API still returns one merged history — a screen that wants
   * the whole story in one list can have it.
   *
   * An entry with nothing readable in it is not drawn either, rather than drawn as a blank line
   * with a marker beside it.
   */
  const readable = entries
    .filter((entry) => entry.kind !== 'COMMENT')
    .map((entry) => ({ entry, text: sentence(entry, names) }))
    .filter((line) => line.text !== '');

  return (
    <ol className="timeline">
      {readable.map(({ entry, text }) => (
        <li key={entry.id} className="timeline__item">
          <span className="timeline__marker" aria-hidden="true" />
          <div className="timeline__body">
            <span className="cell-inline">
              <span className="strong small">{who(entry)}</span>
              <span className="muted small">{formatRelative(entry.occurredAt)}</span>
            </span>
            <p className="timeline__text">{text}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * One line for one thing that happened, in the tenant's language.
 *
 * Empty when there is nothing a person can read, and the entry is then not drawn at all. The
 * alternative is what this screen used to do: an audit row carrying `comment_id` and `mentions`
 * was rendered field by field, so a comment appeared under itself as "mentions: vazio → 0;
 * comment_id: vazio → definido". Internal column names are not a sentence, and a history nobody
 * can read is worse than a shorter one.
 */
function sentence(entry: ActivityEntry, names: Map<string, string>): string {
  if (entry.kind === 'COMMENT') return entry.body ?? '';

  if (entry.kind === 'TIME') {
    return t('work.loggedTime').replace('{n}', String(entry.minutes ?? 0));
  }

  const changed = Object.keys(entry.after ?? {})
    .filter((field) => {
      const before = (entry.before ?? {})[field] ?? null;
      const after = (entry.after ?? {})[field] ?? null;

      // A field the audit carried but nobody changed is not something that happened.
      if (JSON.stringify(before) === JSON.stringify(after)) return false;

      // And a field this screen has no name for is not something a person can read: the action's
      // own phrase says more than `comment_id` does.
      return fieldLabel(field) !== field;
    })
    .map((field) => {
      const before = (entry.before ?? {})[field];
      const after = (entry.after ?? {})[field];

      return t('work.changedField')
        .replace('{field}', fieldLabel(field))
        .replace('{from}', display(before, names, field))
        .replace('{to}', display(after, names, field));
    })
    .join('; ');

  if (changed) return changed;

  // Nothing readable in the fields: name the action instead, and say nothing at all rather than
  // printing its code.
  const phrase = t(`audit.action.${entry.action}`);

  return phrase === `audit.action.${entry.action}` ? '' : phrase;
}

/**
 * The name of a field a change touched.
 *
 * A switch of literal keys rather than a computed one, so the dictionary guard can see every label
 * this screen can ask for. A field nobody has named yet shows its own name, which is ugly but true.
 */
function fieldLabel(field: string): string {
  // The audit writes whichever spelling the call site used, so both are accepted here.
  switch (field) {
    case 'title':
      return t('work.field.title');
    case 'description':
      return t('work.field.description');
    case 'status':
      return t('work.field.status');
    case 'priority':
      return t('work.field.priority');
    case 'assigneeId':
    case 'assignee_id':
      return t('work.field.assigneeId');
    case 'dueDate':
    case 'due_date':
      return t('work.field.dueDate');
    case 'startDate':
    case 'start_date':
      return t('work.field.startDate');
    case 'estimatedMinutes':
    case 'estimated_minutes':
      return t('work.field.estimatedMinutes');
    case 'itemType':
    case 'item_type':
      return t('work.field.itemType');
    case 'storyPoints':
    case 'story_points':
      return t('work.field.storyPoints');
    case 'sprintId':
    case 'sprint_id':
      return t('work.field.sprintId');
    case 'epicId':
    case 'epic_id':
      return t('work.field.epicId');
    case 'projectId':
    case 'project_id':
      return t('work.field.projectId');
    default:
      return field;
  }
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The enum fields, and the dictionary prefix each one's values are named under. */
const ENUM_PREFIX: Record<string, string> = {
  status: 'work.status.',
  priority: 'work.priority.',
  itemType: 'work.item.',
  item_type: 'work.item.',
};

/**
 * One value in a change, as a person reads it.
 *
 * Three things it must never do, all of which it did: print `TODO` where the rest of the product
 * says "A fazer"; print an instant as the ISO string the database keeps; and print `[object Object]`
 * for anything that is not a scalar. The last one was reaching the screen because the audit's own
 * redaction was flattening every `Date` into `{}` — fixed at the source, but this is a snapshot of
 * whatever was written months ago, so it stays defensive about what it finds.
 */
function display(value: unknown, names: Map<string, string>, field = ''): string {
  if (value === null || value === undefined || value === '') return t('work.empty');
  if (typeof value === 'boolean') return value ? t('common.yes') : t('common.no');

  // An object in a snapshot is either an old date the redactor ate or something nobody can read.
  if (typeof value === 'object') return t('work.someValue');

  const text = String(value);

  const prefix = ENUM_PREFIX[field];

  if (prefix) {
    const label = t(`${prefix}${text}`);

    if (label !== `${prefix}${text}`) return label;
  }

  if (ISO_INSTANT.test(text)) return formatDateTime(text);
  if (ISO_DAY.test(text)) return formatDate(text);

  if (!UUID.test(text)) return text;

  // A person or a sprint this screen already knows by name; otherwise say it was set, not which.
  return names.get(text) ?? t('work.someValue');
}

/** A timestamp, and a calendar day, as the API writes them. */
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A comment's author, by id.
 *
 * Falls back to "Sistema" rather than to the raw id: a comment written by an automation is a real
 * thing, and a uuid in the byline reads like a bug.
 */
function Author({ userId }: { userId: string | null }): React.JSX.Element {
  const { options } = useMembers();
  const found = userId === null ? undefined : options.find((person) => person.id === userId);

  if (!found) return <span className="muted small">{t('work.systemAuthor')}</span>;

  return <Person size="xs" name={found.name} url={found.avatarUrl} />;
}

/** Minutes read as hours past an hour; below that the minutes are what somebody wants. */
/**
 * Who the task is for, and who else is following it.
 *
 * Both read from the tenant's own member list, because an id is not a person. The list is a query of
 * its own rather than something the task carries: it is the same list on every task and changes far
 * less often, so React Query caches it once for the whole screen.
 *
 * Watching yourself is offered first and separately. It is what people actually do — "keep me posted
 * on this" — and making somebody find their own name in a dropdown to do it is a small daily
 * indignity.
 */
function People({
  taskId,
  onError,
}: {
  taskId: string;
  onError: (caught: unknown) => void;
}): React.JSX.Element {
  const client = useQueryClient();
  const { user } = useAuth();

  const task = useQuery({ queryKey: ['task', taskId], queryFn: () => getTask(taskId) });

  const { options: active, isLoading: loadingMembers } = useMembers();

  const personOf = (userId: string): { name: string; url?: string | null } => {
    const found = active.find((person) => person.id === userId);

    return found ? { name: found.name, url: found.avatarUrl } : { name: userId.slice(0, 8) };
  };

  function refresh(): void {
    void client.invalidateQueries({ queryKey: ['task', taskId] });
    void client.invalidateQueries({ queryKey: ['task-activity', taskId] });
    void client.invalidateQueries({ queryKey: ['tasks'] });
  }

  const assign = useMutation({
    mutationFn: (assigneeId: string | null) => assignTask(taskId, assigneeId),
    onSuccess: refresh,
    onError,
  });

  const watch = useMutation({
    mutationFn: (userId: string) => watchTask(taskId, userId),
    onSuccess: refresh,
    onError,
  });

  const unwatch = useMutation({
    mutationFn: (userId: string) => unwatchTask(taskId, userId),
    onSuccess: refresh,
    onError,
  });

  const watchers = task.data?.watchers ?? [];
  const meWatching = user ? watchers.includes(user.id) : false;

  return (
    <div className="stack">
      <div className="field">
        <span>{t('work.assignee')}</span>
        <MemberSelect
          value={task.data?.assignee_id ?? null}
          disabled={assign.isPending || loadingMembers}
          placeholder={t('work.unassigned')}
          onChange={(next) => assign.mutate(next)}
        />
      </div>

      <div className="stack__field">
        <span className="muted small">{t('work.watchers')}</span>

        {watchers.length === 0 ? (
          <p className="muted small">{t('work.noWatchers')}</p>
        ) : (
          <ul className="chips">
            {watchers.map((watcher) => (
              <li key={watcher} className="chip chip--person">
                <Avatar size="xs" title={null} {...personOf(watcher)} />
                {personOf(watcher).name}
                <button
                  type="button"
                  className="chip__remove"
                  aria-label={t('work.unwatch')}
                  disabled={unwatch.isPending}
                  onClick={() => unwatch.mutate(watcher)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="cell-inline">
          {user && !meWatching && (
            <button
              type="button"
              className="button button--quiet button--compact"
              disabled={watch.isPending}
              onClick={() => watch.mutate(user.id)}
            >
              {t('work.watchThis')}
            </button>
          )}

          <UserSelect
            value={null}
            disabled={watch.isPending}
            placeholder={t('work.addWatcher')}
            // Already watching is not a choice; offering it would be offering a no-op.
            options={active.filter((person) => !watchers.includes(person.id))}
            onChange={(next) => {
              if (next) watch.mutate(next);
            }}
          />
        </div>
      </div>
    </div>
  );
}

function hours(minutes: number): string {
  if (minutes < 60) return t('work.minutesShort').replace('{n}', String(minutes));

  const whole = Math.floor(minutes / 60);
  const rest = minutes % 60;

  return rest === 0
    ? t('work.hoursShort').replace('{n}', String(whole))
    : `${t('work.hoursShort').replace('{n}', String(whole))} ${t('work.minutesShort').replace('{n}', String(rest))}`;
}
